import { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import Loader from 'ui/src/components/loader/blocks'
import { CreationToolsContext } from '../../manager'
import CreationTool from '../../types/creation-tool'
import CreationToolError from '../../types/error'
import CreationToolOutputType from '../../types/output-type'
import { validateSourceByOutput } from '../../utils/validate-source'
import { ContainerBlock, WrapperBlock } from '../blocks'
import ErrorModal from '../error-modal'
import { Subtitle } from '../typography'
import Buttons from './buttons'
import { CreationToolsWelcomeContext } from './context'
import Copy from './copy'
import Gradient from './copy/gradient'
import Url from './url'

const Container = styled(ContainerBlock as any)`
    background: none;
    padding: 60px 80px 40px;
`

const Content = styled.div`
    position: relative;
    width: 100%;

    ${Subtitle} {
        font-size: 17px;
        font-weight: 900;
    }
`

const Error = styled(ErrorModal)`
    box-shadow: 0 6px 9px rgba(0, 0, 0, 0.5);
    width: 400px;
`

const Peeker = styled.img`
    left: 50%;
    margin-left: -100px;
    pointer-events: none;
    position: absolute;
    top: 0;
    transform: translateY(-50%);
    width: 200px;
`

const Links = styled.div`
    font-size: 14px;
    margin-top: 30px;
    text-align: center;

    a {
        font-weight: bold;
        margin-right: 20px;
    }
`

const CreationToolsWelcome = () => {
    const { tool, setFiles, setIsDraggingFile } = useContext(CreationToolsContext)
    const [error, setError] = useState<CreationToolError>()
    const [errorSource, setErrorSource] = useState<File | string>()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const peekerUrl = CreationTool.peekerUrl(tool)

    useEffect(() => {
        setIsDraggingFile(false)
    }, [error])

    const onGoBack = () => {
        setError(undefined)
        setErrorSource(undefined)
    }

    const onChangeOutputType = async (outputType: CreationToolOutputType) => {
        if (!errorSource) return

        try {
            const validatedFile = await validateSourceByOutput(errorSource, outputType)
            setFiles([validatedFile], outputType)
        } catch (err) {
            setError(err as CreationToolError)
        }
    }

    return (
        <CreationToolsWelcomeContext.Provider
            value={{
                error,
                errorSource,
                isLoading,
                setError,
                setErrorSource,
                setIsLoading,
            }}
        >
            {error ? (
                <WrapperBlock>
                    <Error
                        {...{
                            error,
                            onGoBack,
                            onChangeOutputType,
                        }}
                    />
                </WrapperBlock>
            ) : (
                <>
                    <WrapperBlock>
                        {isLoading ? (
                            <Loader size="large" />
                        ) : (
                            <Container>
                                <Gradient />
                                <Content>
                                    <Copy />
                                    <Buttons />
                                    <Url />
                                    <Links>
                                        <a
                                            href="https://giphy.com/community-guidelines"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Community Guidelines
                                        </a>
                                        <a href="https://giphy.com/privacy" target="_blank" rel="noreferrer">
                                            Privacy Policy
                                        </a>
                                    </Links>
                                </Content>
                                {peekerUrl && <Peeker src={peekerUrl} alt="" />}
                            </Container>
                        )}
                    </WrapperBlock>
                </>
            )}
        </CreationToolsWelcomeContext.Provider>
    )
}

export default CreationToolsWelcome
